import { nextTick } from 'vue'
import { createI18n } from 'vue-i18n'

const _customer = import.meta.env.VITE_CUSTOMER

let supportedLocales = ['de', 'en'];
const defaultLocale = 'de';

const options = {
  locale: defaultLocale,
  allowComposition: true,
  fallbackLocale: defaultLocale, // set fallback locale
  globalInjection: true,
  strategy: 'prefix_except_default'
}

const i18n = createI18n(options)
setI18nLanguage(options.locale)

export function setupI18n(appSettings) {
  supportedLocales = appSettings.SUPPORTED_LOCALES;

  return i18n
}

export { defaultLocale, supportedLocales, i18n };

export function setI18nLanguage(locale) {
  if (i18n.mode === 'legacy') {
    i18n.global.locale = locale
  } else {
    i18n.global.locale.value = locale
  }
  /**
   * NOTE:
   * If you need to specify the language setting for headers, such as the `fetch` API, set it here.
   * The following is an example for axios.
   *
   * axios.defaults.headers.common['Accept-Language'] = locale
   */
  document.querySelector('html').setAttribute('lang', locale)
}

const Trans = {
  defaultLocale: options.fallbackLocale,

  isLocaleSupported(locale: Array<String>) { // <--- 1
    return supportedLocales.includes(locale)
  },

  getUserLocale() { // <--- 2
    const locale = window.navigator.language ||
      window.navigator.userLanguage ||
      Trans.defaultLocale
    return {
      locale: locale,
      localeNoRegion: locale.split('-')[0]
    }
  },

  getPersistedLocale() { // <--- 3
    const persistedLocale = localStorage.getItem("user-locale")

    if(Trans.isLocaleSupported(persistedLocale)) {
      return persistedLocale
    } else {
      return null
    }
  },

  guessDefaultLocale() {
    const userPersistedLocale = Trans.getPersistedLocale()

    if (userPersistedLocale) {
      return userPersistedLocale
    }

    const userPreferredLocale = Trans.getUserLocale()
    if (Trans.isLocaleSupported(userPreferredLocale.locale)) {
      return userPreferredLocale.locale
    }

    if (Trans.isLocaleSupported(userPreferredLocale.localeNoRegion)) {
      return userPreferredLocale.localeNoRegion
    }

    return Trans.defaultLocale
  }
}

export async function switchLanguage(newLocale: String, navigate: Object) {
  await loadLocaleMessages(newLocale)
  Trans.current_locale = newLocale
  setI18nLanguage(newLocale)
  localStorage.setItem("user-locale", newLocale) // <--- add this

  if (navigate) {
    navigate()
  }
}

export async function loadLocaleMessages(locale: String) {
  if(Trans.isLocaleSupported(locale)) {  // <--- 3
    const messages = await import(`@/locales/${locale}.js`)  // <--- 4.1
    i18n.global.setLocaleMessage(locale, messages[locale])  // <--- 5.1

    try {
      const messagesCustom = await import(`@/_customer/${_customer}/locales/${locale}.js`)  // <--- 4.2
      i18n.global.mergeLocaleMessage(locale, messagesCustom[locale]) // <--- 5.2
    } catch (err) {
      console.warn(`[i18n] Customer locale not found for ${_customer}/${locale}, using defaults`, err)
    }
  }

  return nextTick()  // <--- 6
}

export async function routeMiddleware(to, _from, next) {
  const paramLocale = to.params.locale || defaultLocale

  if(!Trans.isLocaleSupported(paramLocale)) {
    return next(Trans.guessDefaultLocale())
  }

  try {
    await switchLanguage(paramLocale)
  } catch (err) {
    console.error('[i18n] Failed to switch language, falling back to default', err)
    await switchLanguage(defaultLocale).catch(() => {})
  }
}
